import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Card, Row, Col, Badge, Spinner, Tab, Tabs, Table, ProgressBar } from 'react-bootstrap';
import { FaUserPlus, FaIdCard, FaFileUpload, FaHistory, FaShieldAlt, FaCheckCircle, FaUniversity, FaCamera, FaCreditCard, FaMoneyBillWave } from 'react-icons/fa';

const AccountOpeningModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('savings_account');
  const [applicationStep, setApplicationStep] = useState(1); // 1=Personal, 2=Documents, 3=Verification, 4=Complete
  const [customerDetails, setCustomerDetails] = useState({
    firstName: '',
    lastName: '',
    fatherName: '',
    motherName: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    mobile: '',
    email: '',
    pan: '',
    aadhar: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    occupation: '',
    annualIncome: ''
  });
  const [documents, setDocuments] = useState({
    panCard: null,
    aadharCard: null,
    photograph: null,
    signature: null,
    incomeProof: null
  });
  const [selectedBank, setSelectedBank] = useState('');
  const [accountType, setAccountType] = useState('savings');
  const [nomineeDetails, setNomineeDetails] = useState({
    name: '',
    relationship: '',
    mobile: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [applications, setApplications] = useState([]);
  const [applicationReference, setApplicationReference] = useState('');

  // Banks offering account opening
  const participatingBanks = [
    { 
      code: 'JANDHAN', 
      name: 'Jan Dhan Account (Any Bank)', 
      minBalance: 0, 
      features: ['Zero Balance', 'RuPay Debit Card', 'Accident Insurance', 'Life Insurance'],
      processingFee: 0,
      processingTime: '2-3 days'
    },
    { 
      code: 'SBI', 
      name: 'State Bank of India', 
      minBalance: 3000, 
      features: ['Online Banking', 'Mobile Banking', 'Debit Card', 'Cheque Book'],
      processingFee: 500,
      processingTime: '5-7 days'
    },
    { 
      code: 'HDFC', 
      name: 'HDFC Bank', 
      minBalance: 10000, 
      features: ['Premium Banking', 'Credit Card Eligibility', 'Investment Services'],
      processingFee: 1000,
      processingTime: '3-5 days'
    },
    { 
      code: 'ICICI', 
      name: 'ICICI Bank', 
      minBalance: 10000, 
      features: ['Digital Banking', 'Investment Platform', 'Insurance Services'],
      processingFee: 1000,
      processingTime: '3-5 days'
    },
    { 
      code: 'AXIS', 
      name: 'Axis Bank', 
      minBalance: 5000, 
      features: ['Online Banking', 'Mobile App', 'Debit Card', 'Cheque Book'],
      processingFee: 750,
      processingTime: '4-6 days'
    },
    { 
      code: 'PNB', 
      name: 'Punjab National Bank', 
      minBalance: 1000, 
      features: ['Basic Banking', 'RuPay Debit Card', 'SMS Banking'],
      processingFee: 300,
      processingTime: '5-7 days'
    },
    { 
      code: 'CANARA', 
      name: 'Canara Bank', 
      minBalance: 1000, 
      features: ['Basic Banking', 'Online Banking', 'Mobile Banking'],
      processingFee: 300,
      processingTime: '5-7 days'
    },
    { 
      code: 'FINO', 
      name: 'Fino Payment Bank', 
      minBalance: 0, 
      features: ['Digital First', 'Zero Balance', 'High Interest', 'No Minimum Balance'],
      processingFee: 0,
      processingTime: '1-2 days'
    }
  ];

  const accountTypes = [
    { key: 'savings_account', label: 'Savings Account', icon: <FaMoneyBillWave className="text-success" /> },
    { key: 'current_account', label: 'Current Account', icon: <FaUniversity className="text-info" /> },
    { key: 'fd_account', label: 'Fixed Deposit', icon: <FaCreditCard className="text-warning" /> }
  ];

  const mockApplications = [
    {
      id: 'ACC001',
      applicantName: 'Rajesh Kumar',
      bank: 'State Bank of India',
      accountType: 'Savings',
      status: 'Approved',
      accountNumber: '123456789012',
      date: '2025-01-10',
      processingFee: 500
    },
    {
      id: 'ACC002',
      applicantName: 'Priya Sharma',
      bank: 'HDFC Bank',
      accountType: 'Savings',
      status: 'Under Review',
      accountNumber: 'Pending',
      date: '2025-01-12',
      processingFee: 1000
    },
    {
      id: 'ACC003',
      applicantName: 'Amit Singh',
      bank: 'Jan Dhan Account',
      accountType: 'Savings',
      status: 'Approved',
      accountNumber: '987654321098',
      date: '2025-01-08',
      processingFee: 0
    }
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setApplicationStep(1);
    setMessage({ type: '', text: '' });
    
    if (tab === 'history') {
      setApplications(mockApplications);
    }
  };

  const validatePersonalDetails = () => {
    const { firstName, lastName, mobile, email, pan, aadhar, dateOfBirth } = customerDetails;
    
    if (!firstName.trim() || !lastName.trim()) return 'Please enter full name';
    if (!/^[6-9]\d{9}$/.test(mobile)) return 'Please enter a valid mobile number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email';
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan)) return 'Please enter a valid PAN number';
    if (!/^\d{12}$/.test(aadhar)) return 'Please enter a valid 12-digit Aadhar number';
    if (!dateOfBirth) return 'Please enter date of birth';
    if (!selectedBank) return 'Please select a bank';
    
    // Check age (must be 18+)
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    const age = today.getFullYear() - birthDate.getFullYear();
    if (age < 18) return 'Applicant must be at least 18 years old';
    
    return null;
  };

  const validateDocuments = () => {
    if (!documents.panCard) return 'Please upload PAN card';
    if (!documents.aadharCard) return 'Please upload Aadhar card';
    if (!documents.photograph) return 'Please upload photograph';
    if (!documents.signature) return 'Please upload signature';
    
    const bank = participatingBanks.find(b => b.code === selectedBank);
    if (bank && bank.minBalance > 5000 && !documents.incomeProof) {
      return 'Income proof required for this account type';
    }
    
    return null;
  };

  const nextStep = () => {
    if (applicationStep === 1) {
      const validation = validatePersonalDetails();
      if (validation) {
        setMessage({ type: 'error', text: validation });
        return;
      }
    } else if (applicationStep === 2) {
      const validation = validateDocuments();
      if (validation) {
        setMessage({ type: 'error', text: validation });
        return;
      }
    }
    
    setApplicationStep(applicationStep + 1);
    setMessage({ type: '', text: '' });
  };

  const prevStep = () => {
    setApplicationStep(applicationStep - 1);
    setMessage({ type: '', text: '' });
  };

  const submitApplication = async () => {
    setLoading(true);
    setMessage({ type: 'info', text: 'Submitting account opening application...' });

    try {
      // Simulate application submission
      await new Promise(resolve => setTimeout(resolve, 4000));

      const bank = participatingBanks.find(b => b.code === selectedBank);
      const referenceNumber = 'ACC' + Date.now();

      const applicationData = {
        referenceNumber: referenceNumber,
        applicantName: `${customerDetails.firstName} ${customerDetails.lastName}`,
        bank: bank.name,
        accountType: accountType === 'savings' ? 'Savings' : accountType === 'current' ? 'Current' : 'Fixed Deposit',
        mobile: customerDetails.mobile,
        email: customerDetails.email,
        processingFee: bank.processingFee,
        expectedProcessingTime: bank.processingTime,
        status: 'Under Review',
        timestamp: new Date().toLocaleString()
      };

      setApplicationReference(referenceNumber);
      setApplicationStep(4);
      setMessage({ 
        type: 'success', 
        text: `Application submitted successfully! Reference Number: ${referenceNumber}` 
      });

      if (onSuccess) {
        onSuccess(applicationData);
      }

    } catch (error) {
      setMessage({ type: 'error', text: 'Application submission failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (documentType, file) => {
    if (file && file.size > 5 * 1024 * 1024) { // 5MB limit
      setMessage({ type: 'error', text: 'File size should not exceed 5MB' });
      return;
    }
    
    setDocuments({ ...documents, [documentType]: file });
    setMessage({ type: 'success', text: `${documentType} uploaded successfully` });
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Approved': 'success',
      'Under Review': 'warning',
      'Rejected': 'danger',
      'Pending': 'secondary'
    };
    return <Badge bg={variants[status]}>{status}</Badge>;
  };

  const getStepProgress = () => {
    return ((applicationStep - 1) / 3) * 100;
  };

  const handleReset = () => {
    setApplicationStep(1);
    setCustomerDetails({
      firstName: '', lastName: '', fatherName: '', motherName: '', dateOfBirth: '',
      gender: '', maritalStatus: '', mobile: '', email: '', pan: '', aadhar: '',
      address: '', city: '', state: '', pincode: '', occupation: '', annualIncome: ''
    });
    setDocuments({ panCard: null, aadharCard: null, photograph: null, signature: null, incomeProof: null });
    setNomineeDetails({ name: '', relationship: '', mobile: '', address: '' });
    setSelectedBank('');
    setApplicationReference('');
    setMessage({ type: '', text: '' });
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaUserPlus className="me-2" />
          Bank Account Opening Service
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Account Type Tabs */}
        <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-4">
          {accountTypes.map((type) => (
            <Tab 
              key={type.key}
              eventKey={type.key} 
              title={
                <span>
                  {type.icon}
                  <span className="ms-2">{type.label}</span>
                </span>
              }
            >
              <div className="p-2">
                <h6 className="text-primary mb-3">{type.label} Opening</h6>
              </div>
            </Tab>
          ))}
          <Tab eventKey="history" title={
            <span>
              <FaHistory className="me-2" />
              Application History
            </span>
          }>
            <div className="p-2">
              <h6 className="text-primary mb-3">Account Opening History</h6>
            </div>
          </Tab>
        </Tabs>

        {/* Application History */}
        {activeTab === 'history' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6>Recent Account Opening Applications</h6>
              <Badge bg="primary">Total: 3 Applications</Badge>
            </div>
            
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Application ID</th>
                  <th>Applicant Name</th>
                  <th>Bank</th>
                  <th>Account Type</th>
                  <th>Account Number</th>
                  <th>Processing Fee</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((application) => (
                  <tr key={application.id}>
                    <td>{application.id}</td>
                    <td>{application.applicantName}</td>
                    <td>{application.bank}</td>
                    <td>{application.accountType}</td>
                    <td>{application.accountNumber}</td>
                    <td>₹{application.processingFee}</td>
                    <td>{getStatusBadge(application.status)}</td>
                    <td>{application.date}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Account Opening Application */}
        {activeTab !== 'history' && (
          <div>
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Application Progress</span>
                <span>{applicationStep}/4</span>
              </div>
              <ProgressBar now={getStepProgress()} variant="primary" />
              <div className="d-flex justify-content-between mt-1">
                <small className={applicationStep >= 1 ? 'text-primary' : 'text-muted'}>Personal Details</small>
                <small className={applicationStep >= 2 ? 'text-primary' : 'text-muted'}>Documents</small>
                <small className={applicationStep >= 3 ? 'text-primary' : 'text-muted'}>Verification</small>
                <small className={applicationStep >= 4 ? 'text-primary' : 'text-muted'}>Complete</small>
              </div>
            </div>

            {/* Step 1: Personal Details */}
            {applicationStep === 1 && (
              <Row>
                <Col md={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <FaIdCard className="me-2" />
                      Personal Information
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>First Name *</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Enter first name"
                              value={customerDetails.firstName}
                              onChange={(e) => setCustomerDetails({...customerDetails, firstName: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Last Name *</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Enter last name"
                              value={customerDetails.lastName}
                              onChange={(e) => setCustomerDetails({...customerDetails, lastName: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Father's Name</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Enter father's name"
                              value={customerDetails.fatherName}
                              onChange={(e) => setCustomerDetails({...customerDetails, fatherName: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Mother's Name</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Enter mother's name"
                              value={customerDetails.motherName}
                              onChange={(e) => setCustomerDetails({...customerDetails, motherName: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Date of Birth *</Form.Label>
                            <Form.Control
                              type="date"
                              value={customerDetails.dateOfBirth}
                              onChange={(e) => setCustomerDetails({...customerDetails, dateOfBirth: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Gender</Form.Label>
                            <Form.Select
                              value={customerDetails.gender}
                              onChange={(e) => setCustomerDetails({...customerDetails, gender: e.target.value})}
                            >
                              <option value="">Select</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="other">Other</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Marital Status</Form.Label>
                            <Form.Select
                              value={customerDetails.maritalStatus}
                              onChange={(e) => setCustomerDetails({...customerDetails, maritalStatus: e.target.value})}
                            >
                              <option value="">Select</option>
                              <option value="single">Single</option>
                              <option value="married">Married</option>
                              <option value="divorced">Divorced</option>
                              <option value="widowed">Widowed</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Mobile Number *</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="10-digit mobile number"
                              value={customerDetails.mobile}
                              onChange={(e) => setCustomerDetails({
                                ...customerDetails, 
                                mobile: e.target.value.replace(/\D/g, '').slice(0, 10)
                              })}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Email Address *</Form.Label>
                            <Form.Control
                              type="email"
                              placeholder="Enter email address"
                              value={customerDetails.email}
                              onChange={(e) => setCustomerDetails({...customerDetails, email: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>PAN Number *</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="10-character PAN"
                              value={customerDetails.pan}
                              onChange={(e) => setCustomerDetails({
                                ...customerDetails, 
                                pan: e.target.value.toUpperCase().slice(0, 10)
                              })}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Aadhar Number *</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="12-digit Aadhar number"
                              value={customerDetails.aadhar}
                              onChange={(e) => setCustomerDetails({
                                ...customerDetails, 
                                aadhar: e.target.value.replace(/\D/g, '').slice(0, 12)
                              })}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3">
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="Complete address"
                          value={customerDetails.address}
                          onChange={(e) => setCustomerDetails({...customerDetails, address: e.target.value})}
                        />
                      </Form.Group>

                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>City</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="City"
                              value={customerDetails.city}
                              onChange={(e) => setCustomerDetails({...customerDetails, city: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>State</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="State"
                              value={customerDetails.state}
                              onChange={(e) => setCustomerDetails({...customerDetails, state: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Pincode</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="6-digit pincode"
                              value={customerDetails.pincode}
                              onChange={(e) => setCustomerDetails({
                                ...customerDetails, 
                                pincode: e.target.value.replace(/\D/g, '').slice(0, 6)
                              })}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Occupation</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Your occupation"
                              value={customerDetails.occupation}
                              onChange={(e) => setCustomerDetails({...customerDetails, occupation: e.target.value})}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Annual Income</Form.Label>
                            <Form.Select
                              value={customerDetails.annualIncome}
                              onChange={(e) => setCustomerDetails({...customerDetails, annualIncome: e.target.value})}
                            >
                              <option value="">Select income range</option>
                              <option value="0-100000">Below ₹1 Lakh</option>
                              <option value="100000-300000">₹1-3 Lakhs</option>
                              <option value="300000-500000">₹3-5 Lakhs</option>
                              <option value="500000-1000000">₹5-10 Lakhs</option>
                              <option value="1000000+">Above ₹10 Lakhs</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <FaUniversity className="me-2" />
                      Select Bank & Account Type
                    </Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Choose Bank *</Form.Label>
                        <Form.Select
                          value={selectedBank}
                          onChange={(e) => setSelectedBank(e.target.value)}
                        >
                          <option value="">Select a bank...</option>
                          {participatingBanks.map((bank) => (
                            <option key={bank.code} value={bank.code}>
                              {bank.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      {selectedBank && (
                        <Card className="mb-3">
                          <Card.Body>
                            {(() => {
                              const bank = participatingBanks.find(b => b.code === selectedBank);
                              return (
                                <div>
                                  <h6>{bank.name}</h6>
                                  <div className="mb-2"><strong>Minimum Balance:</strong> ₹{bank.minBalance.toLocaleString()}</div>
                                  <div className="mb-2"><strong>Processing Fee:</strong> ₹{bank.processingFee}</div>
                                  <div className="mb-2"><strong>Processing Time:</strong> {bank.processingTime}</div>
                                  <div className="mb-2"><strong>Features:</strong></div>
                                  <ul className="mb-0">
                                    {bank.features.map((feature, index) => (
                                      <li key={index} className="small">{feature}</li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })()}
                          </Card.Body>
                        </Card>
                      )}

                      <Form.Group className="mb-3">
                        <Form.Label>Account Type</Form.Label>
                        <Form.Select
                          value={accountType}
                          onChange={(e) => setAccountType(e.target.value)}
                        >
                          <option value="savings">Savings Account</option>
                          <option value="current">Current Account</option>
                          <option value="fd">Fixed Deposit</option>
                        </Form.Select>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Step 2: Document Upload */}
            {applicationStep === 2 && (
              <Card>
                <Card.Header>
                  <FaFileUpload className="me-2" />
                  Document Upload
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-4">
                        <Form.Label>PAN Card *</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileUpload('panCard', e.target.files[0])}
                        />
                        {documents.panCard && (
                          <Badge bg="success" className="mt-1">✓ Uploaded</Badge>
                        )}
                      </div>

                      <div className="mb-4">
                        <Form.Label>Aadhar Card *</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileUpload('aadharCard', e.target.files[0])}
                        />
                        {documents.aadharCard && (
                          <Badge bg="success" className="mt-1">✓ Uploaded</Badge>
                        )}
                      </div>

                      <div className="mb-4">
                        <Form.Label>Photograph *</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUpload('photograph', e.target.files[0])}
                        />
                        {documents.photograph && (
                          <Badge bg="success" className="mt-1">✓ Uploaded</Badge>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <Form.Label>Signature *</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileUpload('signature', e.target.files[0])}
                        />
                        {documents.signature && (
                          <Badge bg="success" className="mt-1">✓ Uploaded</Badge>
                        )}
                      </div>

                      <div className="mb-4">
                        <Form.Label>Income Proof (if required)</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileUpload('incomeProof', e.target.files[0])}
                        />
                        {documents.incomeProof && (
                          <Badge bg="success" className="mt-1">✓ Uploaded</Badge>
                        )}
                        <Form.Text className="text-muted">
                          Salary slip, ITR, or bank statement
                        </Form.Text>
                      </div>
                    </Col>
                  </Row>

                  <Alert variant="info">
                    <FaShieldAlt className="me-2" />
                    All documents should be clear and readable. Maximum file size: 5MB per document.
                  </Alert>
                </Card.Body>
              </Card>
            )}

            {/* Step 3: Verification & Nominee */}
            {applicationStep === 3 && (
              <Row>
                <Col md={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <FaCheckCircle className="me-2" />
                      Application Review
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-2"><strong>Applicant:</strong> {customerDetails.firstName} {customerDetails.lastName}</div>
                      <div className="mb-2"><strong>Mobile:</strong> {customerDetails.mobile}</div>
                      <div className="mb-2"><strong>Email:</strong> {customerDetails.email}</div>
                      <div className="mb-2"><strong>PAN:</strong> {customerDetails.pan}</div>
                      <div className="mb-2"><strong>Aadhar:</strong> ****{customerDetails.aadhar.slice(-4)}</div>
                      {(() => {
                        const bank = participatingBanks.find(b => b.code === selectedBank);
                        return (
                          <div>
                            <div className="mb-2"><strong>Bank:</strong> {bank?.name}</div>
                            <div className="mb-2"><strong>Account Type:</strong> {accountType}</div>
                            <div className="mb-2"><strong>Processing Fee:</strong> ₹{bank?.processingFee}</div>
                          </div>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card>
                    <Card.Header>
                      Nominee Details (Optional)
                    </Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Nominee Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter nominee name"
                          value={nomineeDetails.name}
                          onChange={(e) => setNomineeDetails({...nomineeDetails, name: e.target.value})}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Relationship</Form.Label>
                        <Form.Select
                          value={nomineeDetails.relationship}
                          onChange={(e) => setNomineeDetails({...nomineeDetails, relationship: e.target.value})}
                        >
                          <option value="">Select relationship</option>
                          <option value="spouse">Spouse</option>
                          <option value="father">Father</option>
                          <option value="mother">Mother</option>
                          <option value="son">Son</option>
                          <option value="daughter">Daughter</option>
                          <option value="brother">Brother</option>
                          <option value="sister">Sister</option>
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Mobile Number</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Nominee mobile number"
                          value={nomineeDetails.mobile}
                          onChange={(e) => setNomineeDetails({
                            ...nomineeDetails, 
                            mobile: e.target.value.replace(/\D/g, '').slice(0, 10)
                          })}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Nominee address"
                          value={nomineeDetails.address}
                          onChange={(e) => setNomineeDetails({...nomineeDetails, address: e.target.value})}
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Step 4: Completion */}
            {applicationStep === 4 && (
              <Card>
                <Card.Header className="bg-success text-white">
                  <FaCheckCircle className="me-2" />
                  Application Submitted Successfully
                </Card.Header>
                <Card.Body className="text-center">
                  <FaCheckCircle className="text-success mb-3" size={60} />
                  <h4 className="mb-3">Account Opening Application Submitted!</h4>
                  <div className="mb-3">
                    <strong>Reference Number: {applicationReference}</strong>
                  </div>
                  {(() => {
                    const bank = participatingBanks.find(b => b.code === selectedBank);
                    return (
                      <div>
                        <p>Your application for {bank?.name} {accountType} account has been submitted successfully.</p>
                        <Alert variant="info">
                          <strong>What's Next?</strong><br/>
                          • You will receive SMS/email updates<br/>
                          • Expected processing time: {bank?.processingTime}<br/>
                          • Processing fee: ₹{bank?.processingFee}<br/>
                          • Keep your reference number for tracking
                        </Alert>
                      </div>
                    );
                  })()}
                </Card.Body>
              </Card>
            )}
          </div>
        )}

        {/* Message */}
        {message.text && (
          <Alert variant={
            message.type === 'error' ? 'danger' : 
            message.type === 'info' ? 'info' : 'success'
          } className="mt-3">
            {message.text}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {activeTab !== 'history' && applicationStep > 1 && applicationStep < 4 && (
              <Button variant="outline-secondary" onClick={prevStep}>
                Previous
              </Button>
            )}
            {activeTab !== 'history' && applicationStep === 1 && (
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Close
            </Button>
            {activeTab !== 'history' && applicationStep < 3 && (
              <Button variant="primary" onClick={nextStep}>
                Next
              </Button>
            )}
            {activeTab !== 'history' && applicationStep === 3 && (
              <Button 
                variant="success" 
                onClick={submitApplication}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaUserPlus className="me-2" />
                    Submit Application
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AccountOpeningModal;
